import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiGet, apiPost, apiDel } from '../api';
import ExpandableText from '../components/ExpandableText';
import FeatureResponseView, { FeatureResponse } from '../components/FeatureResponseView';
import FeatureResponseEditor, { ResponseDraft } from '../components/FeatureResponseEditor';

interface FeatureRequest {
  featureId: string;
  title: string;
  description: string;
  status: 'new' | 'open' | 'in-progress' | 'shipped' | 'closed';
  voteCount: number;
  hasVoted: boolean;
  createdAt: string;
  response?: FeatureResponse;
  // A request we posted ourselves, badged "Chaptery idea" (#1011).
  fromChaptery?: boolean;
}

type Tab = 'voting' | 'shipped';

export default function ChecklistPage() {
  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('voting');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitTitle, setSubmitTitle] = useState('');
  const [submitDescription, setSubmitDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Requests with an editor open. A set, so opening one never drops another's draft.
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  // Unsaved editor text and screenshot, kept when a tab switch unmounts the editor.
  const [drafts, setDrafts] = useState<Record<string, ResponseDraft>>({});
  // The request whose editor just closed: focus goes back to its Edit or Add a response button.
  const [refocusId, setRefocusId] = useState<string | null>(null);

  const fetchFeatures = useCallback(async () => {
    try {
      setError(null);
      const res = await apiGet('/feature-requests');
      const data = await res.body.json() as unknown as { features: FeatureRequest[] };
      setFeatures(data.features);
    } catch {
      setError("Couldn't load feature requests — try refreshing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
    fetchAuthSession().then((session) => {
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];
      setIsSubscriber(
        groups.includes('Subscribers') || groups.includes('Admins') || groups.includes('SuperAdmins')
      );
      setIsAdmin(groups.includes('Admins') || groups.includes('SuperAdmins'));
    }).catch(() => {});
  }, [fetchFeatures]);

  useEffect(() => {
    if (!refocusId) return;
    const card = document.querySelector(`.checklist-card[data-feature-id="${refocusId}"]`);
    card?.querySelector<HTMLElement>('.feature-response-edit, .checklist-add-response')?.focus();
    setRefocusId(null);
  }, [refocusId]);

  const handleVote = async (feature: FeatureRequest) => {
    if (!isSubscriber) return;
    try {
      if (feature.hasVoted) {
        const res = await apiDel(`/feature-requests/${feature.featureId}/vote`);
        const data = await res.body.json() as { voteCount: number };
        setFeatures(prev => prev.map(f =>
          f.featureId === feature.featureId
            ? { ...f, hasVoted: false, voteCount: data.voteCount }
            : f
        ));
      } else {
        const res = await apiPost(`/feature-requests/${feature.featureId}/vote`, {});
        const data = await res.body.json() as { voteCount: number };
        setFeatures(prev => prev.map(f =>
          f.featureId === feature.featureId
            ? { ...f, hasVoted: true, voteCount: data.voteCount }
            : f
        ));
      }
    } catch {
      // silently ignore vote errors — user sees their optimistic update briefly revert
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitTitle.trim() || !submitDescription.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiPost('/feature-requests', { title: submitTitle, description: submitDescription });
      // New requests are held for admin review — don't add to the board optimistically.
      setSubmitTitle('');
      setSubmitDescription('');
      setSubmitSuccess(true);
    } catch {
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (featureId: string) => setEditing(prev => new Set(prev).add(featureId));
  const stopEditing = (featureId: string) => {
    setEditing(prev => {
      const next = new Set(prev);
      next.delete(featureId);
      return next;
    });
    setDrafts(prev => { const next = { ...prev }; delete next[featureId]; return next; });
    setRefocusId(featureId);
  };
  const responseSaved = (featureId: string, response: FeatureResponse | null) => {
    setFeatures(prev => prev.map(f => (f.featureId === featureId ? { ...f, response: response ?? undefined } : f)));
    stopEditing(featureId);
  };

  const votingFeatures = features
    .filter(f => f.status === 'new' || f.status === 'open' || f.status === 'in-progress')
    .sort((a, b) => b.voteCount - a.voteCount);

  const shippedFeatures = features
    .filter(f => f.status === 'shipped')
    .sort((a, b) => b.voteCount - a.voteCount);

  const displayFeatures = activeTab === 'voting' ? votingFeatures : shippedFeatures;

  function timeAgo(isoDate: string): string {
    const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
    if (isNaN(seconds)) return '';
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  return (
    <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Lora, serif', margin: 0 }}>Chaptery Checklist</h1>
          <p style={{ color: 'var(--color-text-muted, #666)', marginTop: '0.25rem' }}>
            Vote for the features you want most
          </p>
        </div>
        {isSubscriber ? (
          <button className="btn-primary" onClick={() => { setSubmitSuccess(false); setSubmitError(null); setShowSubmitModal(true); }}>
            Submit a Request
          </button>
        ) : (
          <a href="/pricing" className="btn-primary" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Subscribe to request →
          </a>
        )}
      </div>

      {/* Partnership intro */}
      <div style={{
        background: 'var(--color-surface, #faf8f3)',
        border: '1px solid var(--color-border, #e6e1d6)',
        borderLeft: '3px solid var(--color-primary, #196773)',
        borderRadius: 10,
        padding: '1.35rem 1.6rem',
        marginBottom: '1.75rem',
        lineHeight: 1.65,
      }}>
        <h2 style={{ fontFamily: 'Lora, serif', margin: '0 0 0.5rem', fontSize: '1.35rem' }}>Let's build Chaptery together</h2>
        {!introExpanded ? (
          <p style={{ margin: '0 0 0.5rem', color: 'var(--color-text, #2c2c2c)' }}>
            I'm an independent developer building Chaptery in the open, with the authors who use it — a professional-grade authoring platform at an accessible price. <strong>Vote</strong> for what matters most and <strong>submit a request</strong> for what's missing; this board is where we build it together.
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 0.8rem', color: 'var(--color-text, #2c2c2c)' }}>
              I started CadeKami Software Solutions along with Chaptery with two things — an idea and a passion to create. I've built Chaptery from the ground up to offer some of the industry's cutting-edge tools to indie authors as well as long-time authors, at an accessible price point.
            </p>
            <p style={{ margin: '0 0 0.8rem', color: 'var(--color-text-muted, #555)' }}>
              Chaptery is a professional-grade authoring platform, built by an independent developer (me!) with more than thirty years of professional software craft behind me. That's the promise: the polish and reliability of professional-grade software, and a direct line to the person who builds it — no big company, no committee, no support-ticket black hole.
            </p>
            <p style={{ margin: '0 0 0.8rem', color: 'var(--color-text-muted, #555)' }}>
              If you're an indie author who holds your work to a high standard, we're aligned. You take your craft seriously; so do I. And if you're a long-time published author with six books under your belt, I'd love to hear what would make you more productive. This board is where we build Chaptery together.
            </p>
            <p style={{ margin: '0 0 0.8rem', color: 'var(--color-text-muted, #555)' }}>
              Nearly every feature here was asked for by someone writing their book right now. <strong>Vote</strong> to tell me what matters most, and <strong>submit a request</strong> to put a new idea in front of me and the community. I read all of it, I build in the open, and when something ships you'll see it move to the <strong>Shipped</strong> tab.
            </p>
            <p style={{ margin: '0 0 0.8rem', color: 'var(--color-text-muted, #555)' }}>
              I'd rather build the ten things you truly need than a hundred I merely imagined — or try to match competitors feature for feature. So tell me what would raise your writing game — a refinement, a whole new capability, or a rough edge that's been slowing you down. You bring the craft and the honest feedback; I bring three decades of engineering and the follow-through. Let's build the authoring platform you actually want.
            </p>
          </>
        )}
        <button
          type="button"
          onClick={() => setIntroExpanded(v => !v)}
          aria-expanded={introExpanded}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary, #196773)', fontWeight: 600, fontSize: '0.9rem' }}
        >
          {introExpanded ? 'Show less ↑' : 'Read the full story →'}
        </button>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--color-border, #e0e0e0)', marginBottom: '1.5rem' }}>
        {(['voting', 'shipped'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem 0',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 600 : 400,
              borderBottom: activeTab === tab ? '2px solid var(--color-primary, #4A90E2)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--color-primary, #4A90E2)' : 'inherit',
            }}
          >
            {tab === 'voting' ? 'Voting' : 'Shipped'}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && (
        <div>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: 'var(--color-surface, #f5f5f5)', borderRadius: 8, padding: '1rem', marginBottom: '0.75rem', height: 80, opacity: 0.6 }} />
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: 8, padding: '1rem', color: '#c00' }}>
          {error}
        </div>
      )}

      {!loading && !error && displayFeatures.length === 0 && (
        <p style={{ color: 'var(--color-text-muted, #888)', textAlign: 'center', padding: '2rem 0' }}>
          {activeTab === 'voting' ? 'No feature requests yet. Be the first to submit one!' : 'No shipped features yet.'}
        </p>
      )}

      {!loading && !error && displayFeatures.map(feature => (
        <div
          key={feature.featureId}
          className="checklist-card"
          data-feature-id={feature.featureId}
          style={{
            display: 'flex',
            gap: '1rem',
            background: 'var(--color-surface, #fff)',
            border: '1px solid var(--color-border, #e0e0e0)',
            borderRadius: 8,
            padding: '1rem',
            marginBottom: '0.75rem',
          }}
        >
          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '1rem' }}>{feature.title}</strong>
              {feature.fromChaptery && <span className="checklist-idea-pill">Chaptery idea</span>}
              {feature.status === 'in-progress' && (
                <span style={{ background: '#e0f7f4', color: '#00796b', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600 }}>
                  In Progress
                </span>
              )}
            </div>
            <ExpandableText text={feature.description} className="checklist-description" />
            {editing.has(feature.featureId) ? (
              <FeatureResponseEditor
                featureId={feature.featureId}
                title={feature.title}
                response={feature.response}
                draft={drafts[feature.featureId]}
                onDraftChange={d => setDrafts(prev => ({ ...prev, [feature.featureId]: d }))}
                onSaved={response => responseSaved(feature.featureId, response)}
                onCancel={() => stopEditing(feature.featureId)}
              />
            ) : feature.response ? (
              <FeatureResponseView
                response={feature.response}
                title={feature.title}
                onEdit={isAdmin ? () => startEditing(feature.featureId) : undefined}
              />
            ) : isAdmin ? (
              <button type="button" className="feature-response-link checklist-add-response" onClick={() => startEditing(feature.featureId)}>
                Add a response
              </button>
            ) : null}
            <span style={{ display: 'block', marginTop: '0.35rem', color: 'var(--color-text-muted, #888)', fontSize: '0.8rem' }}>
              {timeAgo(feature.createdAt)}
            </span>
          </div>

          {/* Vote square */}
          {activeTab === 'shipped' ? (
            <div style={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 56, minWidth: 56, background: '#e8f5e9', border: '2px solid #5CA383', borderRadius: 8, padding: '0.5rem', color: '#5CA383', fontWeight: 700, fontSize: '0.8rem', textAlign: 'center' }}>
              <span style={{ fontSize: '1rem', lineHeight: 1 }}>▲</span>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{feature.voteCount}</span>
              <span style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Shipped</span>
            </div>
          ) : isSubscriber ? (
            <button
              onClick={() => handleVote(feature)}
              style={{
                alignSelf: 'flex-start',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                width: 56, minWidth: 56, cursor: 'pointer',
                background: feature.hasVoted ? '#e8f5e9' : 'var(--color-surface, #f9f9f9)',
                border: feature.hasVoted ? '2px solid #5CA383' : '2px solid var(--color-border, #e0e0e0)',
                borderRadius: 8, padding: '0.5rem',
                color: feature.hasVoted ? '#5CA383' : 'inherit',
              }}
            >
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>▲</span>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{feature.voteCount}</span>
            </button>
          ) : (
            <div style={{ position: 'relative', alignSelf: 'flex-start', width: 56, minWidth: 56 }}>
              <div
                className="checklist-vote-locked"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  width: '100%', height: '100%', minHeight: 64,
                  background: 'var(--color-surface, #f9f9f9)',
                  border: '2px solid var(--color-border, #e0e0e0)',
                  borderRadius: 8, padding: '0.5rem', cursor: 'default', opacity: 0.6,
                }}
              >
                <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>▲</span>
                <span style={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{feature.voteCount}</span>
              </div>
              <div style={{
                position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                background: '#333', color: '#fff', borderRadius: 6, padding: '0.3rem 0.5rem',
                fontSize: '0.75rem', whiteSpace: 'nowrap', opacity: 0,
                transition: 'opacity 0.15s',
              }} className="checklist-vote-tooltip">
                <a href="/pricing" style={{ color: '#9ee6c3', textDecoration: 'none' }}>Subscribe to vote →</a>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Submit Modal */}
      {showSubmitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--color-background, #fff)', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 500, margin: '1rem' }}>
            {submitSuccess ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
                <h2 style={{ marginTop: 0 }}>Thanks for your idea!</h2>
                <p style={{ color: '#666', lineHeight: 1.5 }}>
                  Your request has been submitted and is <strong>pending review</strong>. Once approved, it’ll appear on the board for everyone to vote on.
                </p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { setSubmitSuccess(false); setShowSubmitModal(false); }}
                  style={{ marginTop: '0.5rem' }}
                >
                  Done
                </button>
              </div>
            ) : (
            <>
            <h2 style={{ marginTop: 0 }}>Submit a Feature Request</h2>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Title <span style={{ fontWeight: 400, color: '#888' }}>({submitTitle.length}/120)</span>
              </label>
              <input
                type="text"
                value={submitTitle}
                onChange={e => setSubmitTitle(e.target.value.slice(0, 120))}
                maxLength={120}
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', marginBottom: '1rem', boxSizing: 'border-box' }}
              />
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                Description <span style={{ fontWeight: 400, color: '#888' }}>({submitDescription.length}/2000)</span>
              </label>
              <textarea
                value={submitDescription}
                onChange={e => setSubmitDescription(e.target.value.slice(0, 2000))}
                maxLength={2000}
                required
                rows={5}
                style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', marginBottom: '1rem', boxSizing: 'border-box', resize: 'vertical' }}
              />
              {submitError && <p style={{ color: '#c00', marginBottom: '0.75rem' }}>{submitError}</p>}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowSubmitModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting || !submitTitle.trim() || !submitDescription.trim()}>
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .checklist-vote-locked:hover + .checklist-vote-tooltip { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
